/**
 * @file heroSection.component.tsx
 * @path apps/website/src/components/sections/heroSection.component.tsx
 * @description Refined Hero section with support for scroll-to-section and multiple CTA buttons.
 */

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@sirbastians/utils";
import { Button } from "@sirbastians/ui";
import { VideoPlayer } from "../common";

export interface HeroAction {
  text: string;
  /** If set, navigates to this route */
  link?: string;
  /** If set, smooth-scrolls to this element ID */
  scrollTo?: string;
  variant?: "glass" | "accent" | "secondary" | "outline";
}

export interface HeroSectionProps {
  videoSrc: string;
  posterSrc?: string;
  playbackRate?: number;

  title: string;
  subtitle?: string;
  paragraph?: string;

  /** Legacy single button — still supported */
  buttonText?: string;
  buttonLink?: string;

  /** New: multiple CTA actions */
  actions?: HeroAction[];

  className?: string;
}

const handleScrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const HeroSection: React.FC<HeroSectionProps> = ({
  videoSrc,
  posterSrc,
  playbackRate = 0.8,
  title,
  subtitle,
  paragraph,
  buttonText,
  buttonLink,
  actions,
  className,
}) => {
  // Build resolved actions list — support legacy single button or new actions array
  const resolvedActions: HeroAction[] = actions
    ? actions
    : buttonText
      ? [{ text: buttonText, link: buttonLink, variant: "glass" as const }]
      : [];

  return (
    <section
      className={cn(
        "relative flex w-full items-center justify-center overflow-hidden section-full",
        className,
      )}
    >
      {/* 1. Background Video Layer */}
      <div className="absolute inset-0 z-0 h-full w-full">
        <VideoPlayer
          src={videoSrc}
          poster={posterSrc}
          showControls={false}
          autoPlay
          muted
          loop
          playsInline
          playbackRate={playbackRate}
          className="h-full w-full rounded-none shadow-none scale-110"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-nav-bg/70 via-nav-bg/50 to-nav-bg/70" />
      </div>

      {/* 2. Content Layer */}
      <div className="relative z-20 mx-auto flex max-w-4xl flex-col items-center px-6 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="font-montserrat text-4xl font-extrabold tracking-tight text-nav-text sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1]"
        >
          {title}
          {subtitle && (
            <>
              <br />
              <span className="bg-gradient-to-b from-nav-text via-nav-text/80 to-transparent bg-clip-text text-transparent">
                {subtitle}
              </span>
            </>
          )}
        </motion.h1>

        {paragraph && (
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="mt-6 max-w-2xl text-base font-medium text-nav-text-muted drop-shadow-md sm:text-lg md:text-xl"
          >
            {paragraph}
          </motion.p>
        )}

        {resolvedActions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            {resolvedActions.map((action, i) => {
              const variant = action.variant ?? (i === 0 ? "glass" : "glass");

              if (action.scrollTo) {
                return (
                  <Button
                    key={action.text}
                    variant={variant}
                    size="lg"
                    onClick={() => handleScrollTo(action.scrollTo!)}
                  >
                    {action.text}
                  </Button>
                );
              }

              return (
                <Button key={action.text} variant={variant} size="lg" asChild>
                  <Link to={action.link ?? "#"}>{action.text}</Link>
                </Button>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
};
